import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import style from "./style.module.css";
import { EntCropProductionByRegionItem, EntCategory } from '../api/types';
import { ResponseDataGet } from '../../../../models/responseDataGet';

interface RegionTableProps {
    data?: ResponseDataGet<EntCropProductionByRegionItem> | any;
    isLoading?: boolean;
    selectedRegionName?: string;
}

interface RegionByItemWithExpanded extends EntCropProductionByRegionItem {
    expanded?: boolean;
}

export const RegionTable: React.FC<RegionTableProps> = ({ data, isLoading, selectedRegionName }) => {
    const { t } = useTranslation();
    const [regionsData, setRegionsData] = useState<RegionByItemWithExpanded[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [totalArea, setTotalArea] = useState<number>(0);
    const [avgProductivity, setAvgProductivity] = useState<number>(0);

    React.useEffect(() => {
        console.log("RegionTable API data:", data);
        console.log("Data type:", typeof data);
        console.log("Data keys:", data ? Object.keys(data) : "no data");

        if (!data) {
            console.log("No data provided");
            setRegionsData([]);
            setTotalCount(0);
            setTotalArea(0);
            setAvgProductivity(0);
            return;
        }

        console.log("data.res:", data.res);
        console.log("data.res keys:", data.res ? Object.keys(data.res) : "no res");

        if (!data.res) {
            console.log("No data.res");
            setRegionsData([]);
            setTotalCount(0);
            setTotalArea(0);
            setAvgProductivity(0);
            return;
        }

        // Проверяем различные возможные структуры данных
        let regions: any[] = [];

        if (data.res && Array.isArray(data.res.content)) {
            console.log("Processing data.res.content:", data.res.content.length, "items");
            regions = data.res.content;
        } else if (data.res && data.res.data && Array.isArray(data.res.data)) {
            console.log("Processing data.res.data:", data.res.data.length, "items");
            regions = data.res.data;
        } else if (Array.isArray(data.res)) {
            console.log("data.res is array:", data.res.length, "items");
            regions = data.res;
        } else if (Array.isArray(data)) {
            console.log("data is array:", data.length, "items");
            regions = data as any[];
        } else {
            console.log("Unknown region data structure:", data);
            setRegionsData([]);
            setTotalCount(0);
            setTotalArea(0);
            setAvgProductivity(0);
            return;
        }

        const initialRegions = regions.map(item => ({
            ...item,
            expanded: false
        })) as RegionByItemWithExpanded[];

        setRegionsData(initialRegions);
        const total = initialRegions.reduce((sum, item) => sum + Number(item.totalAmount) || 0, 0);
        const totalAreaSum = initialRegions.reduce((sum, item) => sum + Number(item.totalCleaningArea) || 0, 0);
        const avgProd = totalAreaSum > 0 ? (total / totalAreaSum) * 100 : 0; // переводим в ц/га

        setTotalCount(total);
        setTotalArea(totalAreaSum);
        setAvgProductivity(avgProd);
        console.log("Set regionData:", initialRegions.length, "items, total:", total, "area:", totalAreaSum, "productivity:", avgProd);
    }, [data]);

    const toggleRegion = (index: number) => {
        const updatedRegions = [...regionsData];
        updatedRegions[index].expanded = !updatedRegions[index].expanded;
        setRegionsData(updatedRegions);
    };

    return (
        <div className={style.tableContainer}>
            {selectedRegionName && (
                <div className={style.selectedRegionTitle}>
                    {t('Данные по области')}: <b>{selectedRegionName}</b>
                </div>
            )}
            <div className={style.tableHeader}>
                <h3 className={style.tableTitle}>{t('Данные по регионам')}</h3>
                <div className={style.summaryInfo}>
                    <div className={style.summaryItem}>
                        <span className={style.summaryLabel}>Общий объем:</span>
                        <span className={style.summaryValue}>{totalCount.toLocaleString()} т</span>
                    </div>
                    <div className={style.summaryItem}>
                        <span className={style.summaryLabel}>Общая площадь:</span>
                        <span className={style.summaryValue}>{totalArea.toLocaleString()} га</span>
                    </div>
                    <div className={style.summaryItem}>
                        <span className={style.summaryLabel}>Общая продуктивность:</span>
                        <span className={style.summaryValue}>{avgProductivity.toFixed(2)} ц/га</span>
                    </div>
                </div>
            </div>

            <table className={style.regionTable}>
                <thead>
                    <tr>
                        <th className={style.areaColumn}>{t('Регион/Категория')}</th>
                        <th className={style.countColumn}>{t('Объем (тонн)')}</th>
                        <th className={style.countColumn}>{t('Площадь (га)')}</th>
                        <th className={style.countColumn}>{t('Прод-ть (ц/га)')}</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                                {t('Загрузка...')}
                            </td>
                        </tr>
                    ) : regionsData && regionsData.length > 0 ? (
                        regionsData.map((region, index) => (
                            <React.Fragment key={`region-group-${index}`}>
                                <tr className={style.regionRow}>
                                    <td className={style.regionCell}>
                                        <span
                                            className={style.expandButton}
                                            onClick={() => toggleRegion(index)}
                                        >
                                            {region.expanded ? '−' : '+'}
                                        </span>
                                        {region.nameRu }
                                    </td>
                                    <td className={style.countCell}>
                                        <div>
                                            {region.totalAmount.toLocaleString()}
                                        </div>
                                    </td>
                                    <td className={style.countCell}>
                                        <div>
                                            {region.totalCleaningArea?.toLocaleString() || 0}
                                        </div>
                                    </td>
                                    <td className={style.countCell}>
                                        <div>
                                            {region.totalProductivity?.toFixed(2) || 0}
                                        </div>
                                    </td>
                                </tr>
                                {region.expanded && region.categories?.map((category, subIndex) => (
                                    <tr key={`subitem-${index}-${subIndex}`} className={style.subItemRow}>
                                        <td className={style.subItemCell}>
                                            {category.nameRu || category.nameKy}
                                        </td>
                                        <td className={style.countCell}>
                                            {category.totalAmount.toLocaleString()}
                                        </td>
                                        <td className={style.countCell}>
                                            {category.totalCleaningArea?.toLocaleString() || 0}
                                        </td>
                                        <td className={style.countCell}>
                                            {category.totalProductivity?.toFixed(2) || 0}
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                                {t('Данных нет')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}